import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';

import usePageTitle from '../../hooks/UsePageTitle';
const TaxDueDiligence = () => {
  usePageTitle('Tax Due Diligence');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">Tax Due Diligence</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/financing1.png'
            alt="Tax Due Diligence" 
            className="w-full border-round shadow-2 mb-4" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <p className="text-lg line-height-3">
              Navigating India's complex and evolving tax landscape requires more than just filing returns — 
              it demands a deep, analytical understanding of both past tax behavior and future exposure.
            </p>
            <p className="text-lg line-height-3 mt-3">
              Tax Due Diligence (TDD) is an essential step in transactions, investments, mergers, and acquisitions 
              to uncover hidden tax risks, evaluate compliance quality, and ensure smooth tax transitions.
            </p>
            <p className="text-lg line-height-3 mt-3 font-semibold">
              At SnP Finvisors, our TDD approach is not just about compliance — it's about foresight, negotiation 
              strength, and strategic tax positioning.
            </p>
          </Card>
        </div>

        {/* Why TDD Matters Section */}
        <div className="col-12 mt-5">
          <h2>Why Tax Due Diligence Matters</h2>
          <Divider />
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <p className="text-xl line-height-3">
                Tax exposures can have a significant impact on deal valuation, legal structure, and post-deal 
                integration. Even a seemingly compliant business may:
              </p>
              <ul className="text-xl line-height-3 ml-4">
                <li className="mb-2">Have undisclosed tax liabilities or pending assessments</li>
                <li className="mb-2">Follow aggressive tax practices that won't hold up during audits</li>
                <li className="mb-2">Miss out on available tax credits or incentives</li>
                <li>Face future disputes due to poor documentation or interpretation</li>
              </ul>
              <p className="text-xl line-height-3 mt-3">
                Tax issues, if ignored, can lead to post-deal surprises, penalties, litigations, and even 
                reputational damage.
              </p>
            </div>
            <img 
              src='/images/financing2.png' 
              alt="Tax Compliance Analysis" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Scope Section */}
        <div className="col-12 mt-5">
          <h2>Scope of Our Tax Due Diligence</h2>
          <p className="text-xl mb-4">
            SnP Finvisors conducts a 360° tax health check covering direct, indirect, and international tax matters, 
            ensuring a transparent view of all historical, current, and potential exposures.
          </p>
          <Divider />
          
          <div className="grid">
            {/* Scope Item 1 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="1. Income Tax Review" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Scrutiny of past 3–5 years' ITRs, tax audit reports, and computation details</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Assessment of revenue recognition vs. taxable income trends</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Disallowance risks under section 40A(2), 14A, 37, 43B, etc.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>MAT/AMT positions and carry-forward losses</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Scope Item 2 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="2. TDS/TCS Compliance" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Verification of deduction, deposit, and return filing discipline</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Delay interest, short deduction, or non-deduction exposure</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Compliance with 194C, 194J, 194H, 195 (NRI payments), and TCS sections</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Reconciliations with Form 26AS and books</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Scope Item 3 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="3. GST Review" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>GST registration health across states/branches</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Matching of GSTR-1, 3B, and GSTR-9/9C with books of accounts</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>ITC mismatches, reversals, blocked credits, and transitional credit risks</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Sector-specific risks: RCM, e-invoicing, job work, place of supply issues</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Scope Item 4 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="4. Litigation & Notices" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of pending tax notices, SCNs, assessments, or appeals</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Analysis of potential contingent liabilities and probable financial impacts</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Verification of outcomes from past audits and likelihood of recurrence</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Scope Item 5 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="5. Transfer Pricing (if applicable)" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Inter-company transactions review (goods, services, loans, royalties)</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>TP study report, benchmarking quality, and documentation under Rule 10D</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Evaluation of arm's length pricing and related party disclosures</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Assessment of risk for future assessments or TP adjustments</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Scope Item 6 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="6. Customs, Excise, VAT Legacy Liabilities" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>In case of older entities, review of historical taxes subsumed by GST</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Status of VAT/CST assessments, refund claims, and demand show-cause notices</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Treatment of pre-GST credits and liabilities in balance sheet</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Scope Item 7 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="7. Tax Provisioning & Reporting" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Accuracy and completeness of tax provisions in financials</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Deferred tax asset/liability analysis and reversals</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Alignment with Ind AS/IFRS requirements</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Scope Item 8 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="8. Impact on Deal Structure" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Recommendations on slump sale vs. share sale vs. asset sale</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Tax-efficient structures based on findings (e.g., spin-offs, buy-backs, capital gains deferral)</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Guidance on indemnities, escrows, and warranties</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>

        {/* Closing Section */}
        <div className="col-12 mt-5">
          <div className="flex flex-column md:flex-row gap-5">
            <img 
              src='/images/financing3.png'
              alt="Tax Strategy" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
            <div className="flex-1">
              <h2>Strategic Tax Insight for Confident Transactions</h2>
              <Divider />
              <p className="text-xl line-height-3">
                At SnP Finvisors, we transform tax due diligence from a compliance checklist into a strategic 
                advantage. Our comprehensive approach identifies not just current exposures but future risks, 
                helping you structure deals with confidence and negotiate from a position of strength.
              </p>
              <p className="text-xl line-height-3 mt-3 font-semibold">
                Because in today's complex tax environment, what you don't know can cost you far more than 
                what you do know.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxDueDiligence;